import { BitkitAlert, BitkitButton, BitkitDialog, BitkitDialogProps } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { ReactNode } from 'react';

export interface ConfirmationDialogProps extends Omit<
  BitkitDialogProps,
  'children' | 'footerButtons' | 'footerContent' | 'onOpenChange' | 'open' | 'state'
> {
  isVisible: boolean;
  text: ReactNode;
  confirmText: string;
  confirmDisabled?: boolean;
  closeText: string;
  onConfirm: () => void;
  onClose: () => void;
  isInProgress: boolean;
  dataTestId?: string;
  isError?: boolean;
  errorMessage?: string | ReactNode;
  isDanger?: boolean;
}

const ConfirmationDialog = ({
  isVisible,
  title,
  text,
  confirmText,
  confirmDisabled = false,
  closeText,
  onConfirm,
  onClose,
  isInProgress,
  dataTestId,
  isError,
  errorMessage,
  isDanger = true,
  ...rest
}: ConfirmationDialogProps) => (
  <BitkitDialog
    data-testid={dataTestId}
    state={isInProgress ? 'loading' : undefined}
    {...rest}
    footerContent={
      isError && errorMessage ? (
        <BitkitAlert variant="critical" action={{ label: 'Retry', onClick: onConfirm }} messageText={errorMessage} />
      ) : undefined
    }
    footerButtons={
      <>
        <BitkitButton
          state={isInProgress ? 'disabled' : undefined}
          variant="secondary"
          onClick={() => !isInProgress && onClose()}
        >
          {closeText}
        </BitkitButton>
        <BitkitButton
          state={isInProgress ? 'loading' : confirmDisabled ? 'disabled' : undefined}
          variant={isDanger ? 'danger-primary' : 'primary'}
          onClick={onConfirm}
        >
          {confirmText}
        </BitkitButton>
      </>
    }
    onOpenChange={({ open }) => !open && onClose()}
    open={isVisible}
    title={title}
  >
    {/* Keeps arbitrary `text` as a single flex item, so multi-element content stays in block flow
        instead of picking up the dialog body's 24px gap between each child. */}
    <Box textStyle="body/lg/regular">{text}</Box>
  </BitkitDialog>
);

export default ConfirmationDialog;
